import React, { createContext, useContext, useReducer, useRef } from 'react';
import { monotonicNow } from '../utils/botCheck';

const initialState = {
  companyOfApplication: '',
  personal: { firstName: '', middleName: '', lastName: '', suffix: '' },
  dateOfBirth: '',
  ssn: '',
  ssnConfirm: '',
  currentAddress: { street: '', city: '', state: '', zip: '', years: '' },
  previousAddress: { street: '', city: '', state: '', zip: '' },
  contact: { homePhone: '', cellPhone: '', email: '' },
  license: {
    number: '', state: '', issueDate: '', class: '', expirationDate: '',
    yearsHeld: '', endorsements: '', restrictions: '', statesOperated: [], lower48: false,
    photoFile: null, photoBase64: null, photoName: null
  },
  yesNo: {
    surety_fidelity_bond: { answer: null },
    felonies: { answer: null, explanation: '' },
    currently_working: { answer: null },
    two_jobs: { answer: null },
    failed_drug_test: { answer: null, explanation: '' },
    refused_drug_test: { answer: null, explanation: '' },
    license_other_states: { answer: null, previousState: '', previousLicenseNumber: '' },
    license_suspended_revoked: { answer: null, explanation: '' },
    denied_license: { answer: null, explanation: '' },
    traffic_convictions: { answer: null, explanation: '' },
    accidents_cmv: { answer: null, explanation: '' },
    fatality_hazmat: { answer: null, explanation: '' },
  },
  jobEntries: [],
  // Seven-day on-duty window. `days` is stamped once, when the driver first
  // reaches the step, and is never recomputed — see utils/dotHours.js.
  dotHours: { answer: null, days: [], anchoredAt: null },
  // Bot defense (item 31). Only the honeypot lives in reducer state — the
  // page-load stamp deliberately does NOT, so that RESET cannot wipe it.
  botCheck: { honeypot: '' },
  signature: { printedName: '', signatureData: null },
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.key]: action.value };
    case 'SET_NESTED':
      return { ...state, [action.key]: { ...state[action.key], ...action.value } };
    case 'SET_YESNO':
      return { ...state, yesNo: { ...state.yesNo, [action.key]: { ...state.yesNo[action.key], ...action.value } } };
    case 'SET_JOB_ENTRIES':
      return { ...state, jobEntries: action.value };
    case 'SET_BOT_CHECK':
      return { ...state, botCheck: { ...state.botCheck, ...action.value } };
    case 'SET_DOT_HOURS':
      return { ...state, dotHours: { ...state.dotHours, ...action.value } };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

const FormContext = createContext(null);

export function FormProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // 🔑 Page-load stamp for the timing floor. A REF, not reducer state, and
  // stamped by the lazy initialiser so it is taken exactly once when the
  // /apply route mounts.
  //
  // It is kept out of the reducer on purpose: RESET (dispatched after a
  // successful submission) returns initialState, which would null a
  // reducer-held stamp. The next submission on that same page load would then
  // arrive with no elapsed time and be rejected as a bot. A ref survives RESET
  // and keeps counting from the real page load, which is exactly right — a
  // second application typed on the same page load is even further from the
  // floor, never closer.
  const formStartedAt = useRef(null);
  if (formStartedAt.current === null) {
    formStartedAt.current = monotonicNow();
  }

  return (
    <FormContext.Provider value={{ state, dispatch, formStartedAt }}>
      {children}
    </FormContext.Provider>
  );
}

export function useForm() {
  return useContext(FormContext);
}
